/**
 * Search input with type-ahead suggestions.
 *
 * Suggestions come from a debounced endpoint that matches books, authors and
 * categories in both languages at once, so a member typing Arabic in the English
 * interface still finds what they want.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { cx } from '@/components/ui'
import { useSuggestions } from '@/hooks/useCatalog'
import { useDebounced } from '@/hooks/useDebounced'

interface SearchBarProps {
  value: string
  onChange: (value: string) => void
  onSubmit: (value: string) => void
  autoFocus?: boolean
  className?: string
}

export const SearchBar = ({
  value,
  onChange,
  onSubmit,
  autoFocus = false,
  className,
}: SearchBarProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const debounced = useDebounced(value, 250)
  const { data: suggestions } = useSuggestions(open ? debounced : '')

  // Dismiss the dropdown on an outside click.
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [])

  const hasSuggestions =
    Boolean(suggestions) &&
    (suggestions!.books.length > 0 ||
      suggestions!.authors.length > 0 ||
      suggestions!.categories.length > 0)

  const submit = (term: string) => {
    setOpen(false)
    onSubmit(term)
  }

  return (
    <div ref={containerRef} className={cx('relative', className)}>
      <form
        role="search"
        onSubmit={(event) => {
          event.preventDefault()
          submit(value)
        }}
      >
        <label htmlFor="catalog-search" className="sr-only">
          {t('search.label')}
        </label>
        <div className="relative">
          {/* Icon sits on the leading edge, which mirrors in Arabic. */}
          <span
            className="pointer-events-none absolute inset-y-0 start-0 grid w-10 place-items-center text-ink-subtle"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
          </span>

          <input
            id="catalog-search"
            type="search"
            value={value}
            autoFocus={autoFocus}
            onChange={(event) => {
              onChange(event.target.value)
              setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            placeholder={t('search.placeholder')}
            autoComplete="off"
            role="combobox"
            aria-expanded={open && hasSuggestions}
            aria-controls="search-suggestions"
            className="w-full rounded-xl border border-border-subtle bg-surface py-2.5 pe-3 ps-10 text-sm text-ink shadow-sm transition placeholder:text-ink-subtle focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25"
          />

          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('')
                submit('')
              }}
              aria-label={t('search.clear')}
              className="absolute inset-y-0 end-0 grid w-10 place-items-center text-ink-subtle transition hover:text-ink"
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </div>
      </form>

      {open && hasSuggestions && (
        <div
          id="search-suggestions"
          role="listbox"
          className="absolute inset-x-0 top-full z-20 mt-1.5 max-h-80 overflow-y-auto rounded-xl border border-border-subtle bg-surface py-1.5 shadow-[var(--shadow-raised)]"
        >
          <SuggestionGroup
            title={t('search.inCategories')}
            items={suggestions!.categories.map((item) => ({
              key: `c-${item.id}`,
              label: item.label,
              onSelect: () => {
                setOpen(false)
                navigate(`/browse?category=${encodeURIComponent(item.slug)}`)
              },
            }))}
          />
          <SuggestionGroup
            title={t('search.inBooks')}
            items={suggestions!.books.map((item) => ({
              key: `b-${item.id}`,
              label: item.label,
              onSelect: () => {
                setOpen(false)
                navigate(`/books/${item.slug}`)
              },
            }))}
          />
          <SuggestionGroup
            title={t('search.inAuthors')}
            items={suggestions!.authors.map((item) => ({
              key: `a-${item.id}`,
              label: item.label,
              onSelect: () => {
                setOpen(false)
                navigate(`/browse?author=${encodeURIComponent(item.slug)}`)
              },
            }))}
          />

          <button
            type="button"
            onClick={() => submit(value)}
            className="mt-1 w-full border-t border-border-subtle px-3 py-2.5 text-start text-sm font-medium text-brand-strong transition hover:bg-brand-soft"
          >
            {t('search.searchFor', { term: value })}
          </button>
        </div>
      )}
    </div>
  )
}

const SuggestionGroup = ({
  title,
  items,
}: {
  title: string
  items: { key: string; label: string; onSelect: () => void }[]
}) => {
  if (items.length === 0) return null
  return (
    <div className="py-1">
      <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-ink-subtle">
        {title}
      </p>
      <ul>
        {items.map((item) => (
          <li key={item.key}>
            <button
              type="button"
              role="option"
              aria-selected={false}
              onClick={item.onSelect}
              className="block w-full truncate bidi-isolate px-3 py-2 text-start text-sm text-ink transition hover:bg-surface-sunken"
            >
              {item.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
